import { CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import qs from "qs";
import React, { useEffect, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";

import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { Chip, pluralize } from "../../helpers/general";
import { useDriverQuery } from "../../hooks/drivers/useDriverQuery";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import DriversTable from "./DriverTable";
import { cardStyle } from "../Places/PlacesTable";
import { controlStyle } from "../../helpers/constants";

export default function Drivers() {
  const { search } = useLocation();
  const navigate = useNavigate();

  const initialPage =
    parseInt(qs.parse(search.replace(/^\?/, "")).page as string) || 1;

  const {
    data,
    isLoading,
    currentPage,
    setCurrentPage,
    pageSize,
    searchHandler,
    removeDriver,
    mutating,
    expirationStatus,
    expirationStatusHandler,
  } = useDriverQuery(undefined, initialPage, "DESC", true);

  // Keep the page number in the URL (other params are preserved)
  useEffect(() => {
    const current = qs.parse(search.replace(/^\?/, ""));
    const next = qs.stringify(
      { ...current, page: String(currentPage) },
      { skipNulls: true },
    );
    navigate(`?${next}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      removeDriver(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const total = data?.meta?.totalItems ?? 0;
  const selectedCount = selected?.ids.length ?? 0;

  return (
    <AdminRoute>
      <section>
        {/* Title + count */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <h5 className="fs-4 fw-bold mb-0">Chauffeurs</h5>
          {data && (
            <Chip tone="warning">
              {total} {pluralize("chauffeur", total)}
            </Chip>
          )}
        </div>

        {/* Toolbar */}
        <div
          style={{ ...cardStyle, padding: "12px 16px" }}
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <div
            className="d-flex align-items-center gap-2 px-3 flex-grow-1"
            style={{ ...controlStyle, minWidth: 200, maxWidth: 320 }}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              type="text"
              placeholder="Rechercher"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              onChange={(e) => searchHandler(e.target.value)}
            />
          </div>

          <select
            className="form-select px-3"
            style={{ ...controlStyle, width: "auto" }}
            value={String(expirationStatus)}
            onChange={(e) => expirationStatusHandler(e.target.value === "true")}
          >
            <option value="false">Tous les documents</option>
            <option value="true">Sur le point d'expirer</option>
          </select>

          <button
            type="button"
            disabled={mutating || selectedCount === 0}
            onClick={handleBulkDelete}
            className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
          >
            {mutating ? (
              <>
                <CSpinner size="sm" /> {general.fr.wait}
              </>
            ) : (
              <>
                <DeleteIcon fill={colors.primary} /> Supprimer
                {selectedCount > 0 && ` (${selectedCount})`}
              </>
            )}
          </button>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading}
            render={() =>
              data?.items?.length > 0 ? (
                <DriversTable
                  drivers={data.items as Driver[]}
                  DriversTableToBeDeleted={dispatch}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          siblingCount={1}
          totalCount={data?.meta?.totalPages}
          pageSize={pageSize}
        />
      </section>
    </AdminRoute>
  );
}
